// Schema-driven decisions for generic pages: which columns to show, what can be sorted and filtered,
// and how a record names itself.
import { truncate } from './format'
import type { EntitySchema, FieldSchema, RecordRow } from './schema'

/** Pseudo-fields for the system timestamps, offered as optional columns on every list. */
export const SYSTEM_COLUMNS: FieldSchema[] = [
  sys('updatedAt', 'Last updated', 'When the record last changed.'),
  sys('createdAt', 'Created', 'When the record was created.'),
]

function sys(name: string, label: string, description: string): FieldSchema {
  return {
    name,
    kind: 'datetime',
    description,
    label,
    required: false,
    unique: false,
    default: null,
    access: 'system',
    list: false,
    filter: false,
    search: false,
    sensitive: false,
  }
}

const NON_SORTABLE = new Set(['json', 'tags', 'refs', 'longtext', 'markdown'])
const NOT_COLUMNABLE = new Set(['json'])

export function isSortable(f: FieldSchema): boolean {
  return !NON_SORTABLE.has(f.kind)
}

/**
 * Columns available on a list page, in display order: key (e.g. DCF code) first, then the display field,
 * then the remaining fields in declaration order, then created/updated timestamps.
 */
export function availableColumns(e: EntitySchema): FieldSchema[] {
  const out: FieldSchema[] = []
  const seen = new Set<string>()
  const push = (f: FieldSchema | undefined) => {
    if (f && !seen.has(f.name) && !NOT_COLUMNABLE.has(f.kind)) {
      out.push(f)
      seen.add(f.name)
    }
  }
  if (e.key) push(e.fields.find((f) => f.name === e.key))
  push(e.fields.find((f) => f.name === e.display))
  for (const f of e.fields) push(f)
  for (const f of SYSTEM_COLUMNS) push(f)
  return out
}

/**
 * Extra default columns for one entity, on top of fields marked list. System columns
 * (updatedAt, createdAt) are allowed. Used when a list needs a timestamp the shared
 * "single column" rule would not add.
 */
export const DEFAULT_COLUMN_EXTRAS: Record<string, readonly string[]> = {
  maintenance_ticket: ['updatedAt'],
}

/** Columns visible by default: fields marked list (plus key and display), else the display field. */
export function defaultVisibleColumns(e: EntitySchema): string[] {
  const listed = e.fields.filter((f) => f.list).map((f) => f.name)
  const set = new Set<string>(listed)
  if (e.key && listed.length) set.add(e.key)
  set.add(e.display)
  if (set.size === 1) set.add('updatedAt')
  for (const name of DEFAULT_COLUMN_EXTRAS[e.name] ?? []) set.add(name)
  return availableColumns(e)
    .map((f) => f.name)
    .filter((n) => set.has(n))
}

export function filterFields(e: EntitySchema): FieldSchema[] {
  return e.fields.filter(
    (f) => f.filter && f.kind !== 'json' && f.kind !== 'longtext' && f.kind !== 'markdown',
  )
}

/** The record's name: its display field, falling back to the key, then the id. */
export function displayOf(
  e: Pick<EntitySchema, 'display' | 'key'>,
  row: Record<string, unknown> | null | undefined,
  max = 120,
): string {
  if (!row) return ''
  const v = row[e.display]
  if (v !== null && v !== undefined && v !== '') return truncate(String(v), max)
  if (e.key && row[e.key]) return String(row[e.key])
  return String(row.id ?? '')
}

/** Ref targets used by the given columns: { targetEntity: [ids] } for display-name resolution. */
export function collectRefIds(fields: FieldSchema[], rows: RecordRow[]): Record<string, string[]> {
  const out: Record<string, Set<string>> = {}
  for (const f of fields) {
    if (f.kind !== 'ref' && f.kind !== 'refs' && f.kind !== 'file') continue
    const target = f.to ?? (f.kind === 'file' ? 'file' : undefined)
    if (!target) continue
    const set = out[target] ?? new Set<string>()
    out[target] = set
    for (const r of rows) {
      const v = r[f.name]
      if (typeof v === 'string' && v) set.add(v)
      else if (Array.isArray(v)) for (const id of v) if (typeof id === 'string' && id) set.add(id)
    }
  }
  return Object.fromEntries(
    Object.entries(out)
      .filter(([, s]) => s.size)
      .map(([k, s]) => [k, [...s].sort()]),
  )
}

/** Next sort when a column header is clicked: ascending, then descending, then back to the default. */
export function nextSort(current: string | undefined, field: string): string | undefined {
  if (current === field) return `-${field}`
  if (current === `-${field}`) return undefined
  return field
}

export function sortDirection(current: string | undefined, field: string): 'asc' | 'desc' | null {
  if (current === field) return 'asc'
  if (current === `-${field}`) return 'desc'
  return null
}

/** Fields shown wide (full row) on the detail page. */
export function isWideField(f: FieldSchema): boolean {
  return f.kind === 'markdown' || f.kind === 'longtext' || f.kind === 'json'
}
